/**
 * S3 Upload Service for handling file uploads with presigned URLs
 * Simplified version using Fetch API
 */

export interface UploadOptions {
    // Function to get a signed URL for uploading
    getSignedUrl?: (
        file: File,
        callback: (result: SignedUrlResult) => void,
    ) => void;
    // Called when upload completes successfully
    onFinish?: (result: SignedUrlResult, file: File) => void;
    // Called when an error occurs
    onError?: (error: string, file: File, context?: ErrorContext) => void;
}

export interface ErrorContext {
    response: string;
    status: number;
    statusText: string;
    readyState: number;
}

export interface SignedUrlResult {
    signedUrl?: string;
    url?: string;
    headers?: Record<string, string>;
    contentType?: string;
}

/**
 * S3Upload class for handling file uploads to S3 via presigned URLs
 */
export class S3Upload {
    private options: UploadOptions;
    private abortController: AbortController | null = null;

    constructor(options: UploadOptions = {}) {
        this.options = options;
    }

    /**
     * Upload a single file using the provided options
     */
    uploadFile(file: File): Promise<SignedUrlResult> {
        return new Promise((resolve, reject) => {
            try {
                // Get the signed URL
                if (!this.options.getSignedUrl) {
                    const error = new Error(
                        "No getSignedUrl function provided",
                    );
                    this.options.onError?.(error.message, file, {
                        response: "",
                        status: 0,
                        statusText: "Configuration Error",
                        readyState: 0,
                    });
                    reject(error);
                    return;
                }

                this.options.getSignedUrl(file, (signResult) => {
                    if (
                        !signResult ||
                        (!signResult.url && !signResult.signedUrl)
                    ) {
                        const error = new Error("Invalid signed URL result");
                        this.options.onError?.(error.message, file, {
                            response: JSON.stringify(signResult),
                            status: 0,
                            statusText: "Invalid URL",
                            readyState: 0,
                        });
                        reject(error);
                        return;
                    }

                    // Get the URL and start the upload
                    this.uploadToS3(file, signResult)
                        .then(() => {
                            this.options.onFinish?.(signResult, file);
                            resolve(signResult);
                        })
                        .catch((error) => {
                            if (error instanceof Error) {
                                this.options.onError?.(error.message, file, {
                                    response: "",
                                    status: 0,
                                    statusText: error.message,
                                    readyState: 0,
                                });
                            }
                            reject(error);
                        });
                });
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : String(error);
                this.options.onError?.(errorMessage, file, {
                    response: "",
                    status: 0,
                    statusText: "Unexpected Error",
                    readyState: 0,
                });
                reject(
                    error instanceof Error ? error : new Error(errorMessage),
                );
            }
        });
    }

    /**
     * Upload a file to S3 using the provided signed URL with Fetch API
     */
    private uploadToS3(file: File, signResult: SignedUrlResult): Promise<void> {
        return new Promise((resolve, reject) => {
            const url = signResult.signedUrl || signResult.url;

            if (!url) {
                const error = new Error("No signed URL provided");
                this.options.onError?.(error.message, file, {
                    response: "",
                    status: 0,
                    statusText: "No URL",
                    readyState: 0,
                });
                reject(error);
                return;
            }

            console.log(`Starting S3 upload to ${url}`);
            console.log(
                `File: ${file.name}, type: ${file.type}, size: ${this.formatBytes(file.size)}`,
            );

            // Create abort controller for cancellation support
            this.abortController = new AbortController();

            // Use fetch API for the upload
            fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type":
                        signResult.contentType ||
                        file.type ||
                        "application/octet-stream",
                },
                body: file,
                mode: "cors",
                credentials: "omit",
                signal: this.abortController.signal,
            })
                .then((response) => {
                    console.log(`Upload response status: ${response.status}`);

                    // Log headers for debugging
                    console.log("Response headers:");
                    response.headers.forEach((value, name) => {
                        console.log(`${name}: ${value}`);
                    });

                    // Check if response is ok (status in the range 200-299)
                    if (!response.ok) {
                        return response.text().then((text) => {
                            console.error("Error response text:", text);
                            throw new Error(
                                `Upload failed: ${response.status} ${response.statusText}`,
                            );
                        });
                    }

                    // For successful responses, just log any response body
                    return response.text().then((text) => {
                        if (text) {
                            console.log(
                                "Response body (first 200 chars):",
                                text.substring(0, 200),
                            );
                        }

                        // Success - upload completed
                        resolve();
                    });
                })
                .catch((error) => {
                    console.error("Upload failed:", error);

                    // Check if this is likely a CORS error
                    if (
                        error.message.includes("NetworkError") ||
                        error.message.includes("Failed to fetch") ||
                        error.message.includes("Network request failed")
                    ) {
                        console.error(
                            "⚠️ This appears to be a CORS issue. Check S3 bucket CORS configuration.",
                        );
                    }

                    this.options.onError?.(error.message, file, {
                        response: error.message,
                        status: 0,
                        statusText: "Network Error",
                        readyState: 0,
                    });
                    reject(error);
                });
        });
    }

    /**
     * Format bytes to human-readable format
     */
    private formatBytes(bytes: number): string {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }

    /**
     * Abort the current upload
     */
    abortUpload(): void {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
}

/**
 * Helper function to upload a file using a presigned URL
 */
export async function uploadFileWithPresignedUrl(
    file: File,
    getPresignedUrl: () => Promise<SignedUrlResult>,
): Promise<SignedUrlResult> {
    try {
        const presignedUrlResult = await getPresignedUrl();

        if (
            !presignedUrlResult ||
            (!presignedUrlResult.url && !presignedUrlResult.signedUrl)
        ) {
            throw new Error("Invalid presigned URL result");
        }

        return new Promise<SignedUrlResult>((resolve, reject) => {
            const uploader = new S3Upload({
                getSignedUrl: (_, callback) => callback(presignedUrlResult),
                onFinish: (result) => resolve(result),
                onError: (error) => reject(new Error(error)),
            });

            uploader.uploadFile(file).catch(reject);
        });
    } catch (error) {
        if (error instanceof Error) {
            return Promise.reject(error);
        }
        return Promise.reject(new Error(String(error)));
    }
}
